import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form"
import { useFieldArray, useForm } from "react-hook-form"

import { useBulkUpsertCustomerPurchaseOrderLineItems } from "./useBulkUpsertCustomerPurchaseOrderLineItems"
import { useGetCustomerPurchaseOrderProducts } from "./useGetCustomerPurchaseOrderProducts"

import type { CompanyPreferredMeasurementSystemEnum } from "@/graphql/codegen/graphql"
import {
  type CustomerPurchaseOrderLineItemsFormValues,
  customerPurchaseOrderLineItemsFormSchema,
} from "@/screens/CustomerOrders/schemas"
import type { SelectedProduct } from "@/screens/CustomerOrders/types"
import {
  applyCustomerPurchaseOrderSaveResult,
  toCpoLineItemFormValues,
  toCustomerPurchaseOrderLineItemsFormValues,
  toCustomerPurchaseOrderProductUpsertInputs,
} from "@/screens/CustomerOrders/utils"

interface UseCustomerPurchaseOrderLineItemsFormProps {
  customerPurchaseOrderId: string
  isExpanded: boolean
  preferredShipFromLocationId: string | null
  preferredMeasurementSystem: CompanyPreferredMeasurementSystemEnum | undefined
  onDirtyChange?: (cpoId: string, isDirty: boolean) => void
}

interface UseCustomerPurchaseOrderLineItemsFormReturn {
  methods: UseFormReturn<CustomerPurchaseOrderLineItemsFormValues>
  lineItemFields: FieldArrayWithId<CustomerPurchaseOrderLineItemsFormValues, "lineItems">[]
  isLoadingLineItems: boolean
  appendLineItems: (products: SelectedProduct[]) => void
  removeLineItem: (lineItemIndex: number) => void
  handleSave: () => void
  isSaving: boolean
  handleDiscard: () => void
  isDiscardConfirmationOpen: boolean
  setIsDiscardConfirmationOpen: (isOpen: boolean) => void
}

export const useCustomerPurchaseOrderLineItemsForm = ({
  customerPurchaseOrderId,
  isExpanded,
  preferredShipFromLocationId,
  preferredMeasurementSystem,
  onDirtyChange,
}: UseCustomerPurchaseOrderLineItemsFormProps): UseCustomerPurchaseOrderLineItemsFormReturn => {
  const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false)
  const [hasLoadedLineItems, setHasLoadedLineItems] = useState(false)

  const methods = useForm<CustomerPurchaseOrderLineItemsFormValues>({
    defaultValues: { lineItems: [] },
    resolver: zodResolver(customerPurchaseOrderLineItemsFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isDirty },
  } = methods

  const { products: savedProducts, hasFetched: hasFetchedLineItems } = useGetCustomerPurchaseOrderProducts(
    customerPurchaseOrderId,
    !isExpanded
  )

  // Must stay before the reset effect below — useFieldArray's own subscription effect needs to be
  // registered first, or a reset firing on the very first commit (e.g. cached query data) is missed.
  const { fields: lineItemFields, append, remove } = useFieldArray({ control: methods.control, name: "lineItems" })

  useEffect(() => {
    if (!isExpanded || hasLoadedLineItems || !hasFetchedLineItems) return

    methods.reset(
      toCustomerPurchaseOrderLineItemsFormValues(savedProducts, {
        preferredShipFromLocationId,
        preferredMeasurementSystem,
      })
    )
    setHasLoadedLineItems(true)
  }, [
    isExpanded,
    hasLoadedLineItems,
    hasFetchedLineItems,
    savedProducts,
    methods,
    preferredShipFromLocationId,
    preferredMeasurementSystem,
  ])

  useEffect(() => {
    onDirtyChange?.(customerPurchaseOrderId, isDirty)
    return () => {
      onDirtyChange?.(customerPurchaseOrderId, false)
    }
  }, [customerPurchaseOrderId, isDirty, onDirtyChange])

  const { bulkUpsertCustomerPurchaseOrderLineItems, isSaving } = useBulkUpsertCustomerPurchaseOrderLineItems()

  const appendLineItems = (products: SelectedProduct[]) => {
    const existingProductIds = new Set(lineItemFields.map((field) => field.productId))
    const newLineItems = products
      .filter((product) => !existingProductIds.has(product.productId))
      .map((product) => toCpoLineItemFormValues(product, { preferredShipFromLocationId, preferredMeasurementSystem }))

    if (newLineItems.length) append(newLineItems)
  }

  const handleSave = methods.handleSubmit(async (formValues) => {
    const savedProducts = await bulkUpsertCustomerPurchaseOrderLineItems({
      customerPurchaseOrderId,
      products: toCustomerPurchaseOrderProductUpsertInputs(formValues),
    })

    if (savedProducts) methods.reset(applyCustomerPurchaseOrderSaveResult(formValues, savedProducts))
  })

  const handleDiscard = () => {
    if (isDirty) setIsDiscardConfirmationOpen(true)
  }

  return {
    methods,
    lineItemFields,
    // Stays true until the fetched products are reset into the form; otherwise one render sees an empty form.
    isLoadingLineItems: isExpanded && !hasLoadedLineItems,
    appendLineItems,
    removeLineItem: remove,
    handleSave,
    isSaving,
    handleDiscard,
    isDiscardConfirmationOpen,
    setIsDiscardConfirmationOpen,
  }
}
